import type { CategoryStructureState } from "@/@types/group-structure";
import type { SituationStatus, StatusBadge, ToastPayload } from "@/@types/unlock-request";
import { UNLOCK_COMMENT_MAX_LENGTH } from "@/constants/access-request";
import { StatusAccessREquest } from "@/enum/status";
import {
  StatusEnumAccessRequest,
  UnlockEntityTypeEnum,
} from "@/enum/access_request";

export const UNLOCK_TAB_LABELS = {
  TAB_ACCESS: "Acessos",
  TAB_UNLOCK: "Desbloqueios",
  CREATE_BUTTON: "Solicitar Desbloqueio",
  SEARCH_PLACEHOLDER: "Buscar por nome ou CPF",
  EMPTY: "Nenhuma solicitação de desbloqueio encontrada.",
  LOAD_FAILED:
    "Não foi possível carregar as solicitações de desbloqueio. Tente novamente.",
  PAGE_OUT_OF_RANGE:
    "Esta página não existe mais. Volte para a primeira página.",
  STORE: (store: string | null): string => (store ? `Loja ${store}` : "-"),
  EMPTY_ELIGIBLE:
    "Nenhum usuário ou operador bloqueado com CPF válido nesta loja.",
  COLUMN_NAME: "Nome",
  COLUMN_TYPE: "Tipo",
  COLUMN_STORE: "Loja",
  COLUMN_CPF: "CPF",
  COLUMN_SITUATION: "Situação",
  COLUMN_STATUS: "Status",
  COLUMN_CREATED_AT: "Data de Criação",
  ACTION_VIEW: "Visualizar",
  VIEW_ROW: (name: string): string => `Visualizar solicitação de ${name}`,
} as const;

export const UNLOCK_PAGINATION_LABELS = {
  ROWS_FOUND: (total: number): string => `${total} linha(s) encontrada(s).`,
  ROWS_PER_PAGE: "Linhas por página",
  PAGE_OF: (current: number, last: number): string =>
    `Página ${current} de ${last}`,
  FIRST: "Ir para a primeira página",
  PREVIOUS: "Ir para a página anterior",
  NEXT: "Ir para a próxima página",
  LAST: "Ir para a última página",
} as const;

export const UNLOCK_ENTITY_LABEL: Record<UnlockEntityTypeEnum, string> = {
  [UnlockEntityTypeEnum.USER]: "Usuário",
  [UnlockEntityTypeEnum.OPERATOR]: "Operador",
};

export const UNLOCK_MODAL_LABELS = {
  TITLE: "Solicitar desbloqueio",
  DESCRIPTION:
    "Selecione o usuário ou operador bloqueado. A situação no Sênior é conferida antes do envio.",
  ENTITY_TYPE: "Tipo de acesso",
  STORE: "Loja",
  STORE_PLACEHOLDER: "Selecione a loja",
  PICK_STORE_FIRST: "Selecione a loja para ver quem está bloqueado.",
  SINGLE_STORE: (label: string): string => `Loja ${label}`,
  NO_STORE: "Nenhuma loja vinculada ao seu usuário. Procure o Financeiro.",
  STORES_FAILED:
    "Não foi possível carregar as suas lojas. Feche e tente novamente.",
  ELIGIBLE_FAILED:
    "Não foi possível consultar os bloqueados desta loja. Tente novamente.",
  LOADING_MORE: "Carregando mais...",
  SHOWING: (shown: number, total: number): string =>
    `Exibindo ${shown} de ${total}. Role para ver mais ou refine a busca.`,
  SELECTED: (name: string): string => `Selecionado: ${name}`,
  COMMENT_TOO_LONG: `A observação pode ter no máximo ${UNLOCK_COMMENT_MAX_LENGTH} caracteres.`,
  SEARCH: "Buscar colaborador",
  COMMENT: "Observação (opcional)",
  COMMENT_PLACEHOLDER: "Contexto para o Financeiro",
  CONFIRM: "Enviar solicitação",
  CANCEL: "Cancelar",
  NOT_WORKING_HINT:
    "Só é possível solicitar para quem está com situação Trabalhando no Sênior.",
  TARGET_REQUIRED: "Selecione quem será desbloqueado.",
  LOADING: "Carregando...",
  CREATE_FALLBACK_ERROR: "Não foi possível enviar a solicitação.",
} as const;

export const UNLOCK_DETAIL_LABELS = {
  TITLE: "Solicitação de Desbloqueio",
  BACK: "Voltar",
  APPROVE: (entity: string): string => `Desbloquear ${entity}`,
  IN_PROGRESS: "Desbloqueio em processamento",
  TARGET_TITLE: "Acesso a desbloquear",
  TARGET_DESCRIPTION:
    "Dados registrados quando a loja abriu a solicitação. A situação no Sênior é conferida de novo no momento do desbloqueio.",
  TYPE: "Tipo",
  CODE: "Código",
  STORE: "Loja",
  WAS_BLOCKED: "Estava bloqueado",
  SITUATION_AT_REQUEST: "Situação na solicitação",
  YES: "Sim",
  NO: "Não",
  UNKNOWN: "-",
  APPROVE_FALLBACK_ERROR: "Não foi possível concluir o desbloqueio.",
  CONFIRM_TITLE: "Confirmar desbloqueio",
  CONFIRM_DESCRIPTION: (
    entity: string,
    name: string,
    code: string,
    store: string,
  ): string =>
    `${entity} ${name} (${code}) será desbloqueado na loja ${store} e a loja será avisada por e-mail. Esta ação não pode ser desfeita.`,
  CONFIRM_COMMENT: "Observação (opcional)",
  CONFIRM_COMMENT_PLACEHOLDER: "Registrada no histórico e enviada à loja",
  CONFIRM_ACTION: "Desbloquear",
  CONFIRM_CANCEL: "Cancelar",
} as const;

export const UNLOCK_TOASTS = {
  CREATED: (name: string): ToastPayload => ({
    variant: "success",
    description: `Solicitação de desbloqueio de ${name} enviada ao Financeiro`,
    duration: 3000,
  }),
  CREATE_FAILED: (description: string): ToastPayload => ({
    variant: "error",
    description,
    duration: 6000,
  }),
  APPROVED: (name: string): ToastPayload => ({
    variant: "success",
    description: `${name} desbloqueado e loja avisada por e-mail`,
    duration: 3000,
  }),
  APPROVE_FAILED: (description: string): ToastPayload => ({
    variant: "error",
    description,
    duration: 6000,
  }),
} as const;

export const UNLOCK_SITUATION_FALLBACK_LABEL: Record<
  Exclude<SituationStatus, "FOUND">,
  string
> = {
  NOT_FOUND: "Não encontrado no Sênior",
  UNAVAILABLE: "Status indisponível",
  INVALID_CPF: "CPF inválido/não informado",
};

export const UNLOCK_STATUS_BADGE: Record<StatusEnumAccessRequest, StatusBadge> = {
  [StatusEnumAccessRequest.PENDING]: { label: "Pendente", variant: "pending" },
  [StatusEnumAccessRequest.APPROVED]: {
    label: "Em processamento",
    variant: "pending",
  },
  [StatusEnumAccessRequest.REJECTED]: {
    label: "Rejeitado",
    variant: "rejected",
  },
  [StatusEnumAccessRequest.COMPLETED]: {
    label: "Desbloqueado",
    variant: "completed",
  },
};

export const UNLOCK_UNKNOWN_STATUS: StatusBadge = {
  label: "Status desconhecido",
  variant: "default",
};

export const ACCESS_REQUEST_STATUS_BADGE: Record<
  StatusAccessREquest,
  { label: string; variant: string }
> = {
  [StatusAccessREquest.APPROVED]: {
    label: "Aprovado",
    variant: "approved",
  },
  [StatusAccessREquest.PENDING]: {
    label: "Pendente",
    variant: "pending",
  },
  [StatusAccessREquest.REJECTED]: {
    label: "Rejeitado",
    variant: "rejected",
  },
  [StatusAccessREquest.COMPLETED]: {
    label: "Completo",
    variant: "completed",
  },
};

export const ACCESS_REQUEST_PAGE_LABELS = {
  STORE_BACK_OFFICE_TITLE: "Requisição de acesso",
  STORE_BACK_OFFICE_DESCRIPTION: "Gerencie as solicitações de acesso dos usuários",
} as const;

export const ACCESS_TAB_LOAD_ERROR_MESSAGE: string = "Erro ao buscar dados";

export const DEVOLUTIVA_MESSAGES = {
  COMMENT_REQUIRED: "Motivo da recusa é obrigatório.",
  TOOLTIP_DISABLED: "Informe o motivo da recusa antes de rejeitar.",
  TOAST_SUCCESS: "Devolutiva enviada com sucesso.",
  TOAST_ERROR: "Erro ao enviar devolutiva.",
  PLACEHOLDER: "Digite o comentário da devolutiva",
  TITLE: "Devolutiva",
  LABEL_COMMENT: "Comentário",
  BUTTON_REJECT: "Rejeitar",
} as const;

export const GROUP_STRUCTURE_MESSAGES = {
  NO_ROUTES: "Nenhuma rota encontrada para este grupo",
} as const;

export const CATEGORY_STATE_LABELS: Record<CategoryStructureState, string> = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  UNREGISTERED: "SEM REGISTRO",
  WITHOUT_ROUTES: "SEM ROTAS",
};

export const ANALYST_ACTION_FALLBACKS = {
  ACTION_FAILED: "Erro ao realizar ação",
  BATCH_FAILED: "Algo deu errado!",
  REVIEW_ITEMS_FAILED: "Erro ao revisar itens. Tente novamente.",
  RESEND_EMAIL_FAILED: "Não foi possível reenviar o email. Tente novamente.",
} as const;
